import React from "react";
import store from "../store";
import { getWebSocket } from "../Socket";
import { Card, Typography, Space, Input, Button, List, Divider } from "antd";
import { MessageOutlined, SendOutlined } from '@ant-design/icons';
import Avatar from './Avatar';

const { Title, Text } = Typography;
const { TextArea } = Input;

interface NotesState {
  chatMessages: { message: string; sender: string }[];
  newMessage: string;
}

class Notes extends React.Component<any, NotesState> {
  private chatMessagesRef: React.RefObject<HTMLDivElement>;
  
  constructor({ props }: { props: any }) {
    super(props);
    this.state = {
      chatMessages: props?.chatMessages ? props.chatMessages : [],
      newMessage: ""
    };
    this.chatMessagesRef = React.createRef();
  }

  componentDidMount(): void {
    // Initialize with current store state
    const currentState = store.getState();
    const currentMessages = this.getStoreChatMessages(currentState);
    this.setState({
      chatMessages: currentMessages
    }, () => {
      // Initial scroll to bottom if messages already exist
      setTimeout(() => {
        if (currentMessages.length > 0) {
          this.scrollToBottom();
        }
      }, 100);
    });

    // Subscribe to future changes
    const unsubscribe = store.subscribe(() => {
      let state = store.getState();
      const newMessages = this.getStoreChatMessages(state);
      const prevMessageCount = this.state.chatMessages.length;
      
      this.setState({
        chatMessages: newMessages
      }, () => {
        // Auto-scroll to bottom when new messages arrive
        if (newMessages.length > prevMessageCount) {
          this.scrollToBottom();
        }
      });
    });
  }

  componentDidUpdate(prevProps: any, prevState: NotesState): void {
    // Force re-render when modal becomes visible
    if (prevState.chatMessages.length !== this.state.chatMessages.length) {
      setTimeout(() => this.scrollToBottom(), 50);
    }
  }

  getStoreChatMessages(state: any): { message: string; sender: string }[] {
    return state.kingGame.gameState.chatMessages || [];
  }

  scrollToBottom = () => {
    if (this.chatMessagesRef.current) {
      this.chatMessagesRef.current.scrollTop = this.chatMessagesRef.current.scrollHeight;
    }
  }

  sendMessage = () => {
    let newMessage: string = this.state.newMessage.trim();
    if (!newMessage) return;

    this.setState({
      newMessage: ""
    });

    let data = {
      "id": "sendChatMessage",
      "message": newMessage
    };
    const ws = getWebSocket();
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(data));
    }
    
    // Scroll to bottom after sending message
    setTimeout(() => this.scrollToBottom(), 100);
  }

  onValueChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    this.setState({
      newMessage: e.target.value
    })
  }

  handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      this.sendMessage();
    }
  }

  render(): React.ReactNode {
    const { chatMessages, newMessage } = this.state;

    return (
      <Card
        className="glass-effect"
        style={{
          borderRadius: 'var(--radius-lg)',
          border: '1px solid var(--border-color)',
          background: 'rgba(30, 41, 59, 0.8)',
          backdropFilter: 'blur(10px)',
          height: '100%',
          display: 'flex',
          flexDirection: 'column'
        }}
        bodyStyle={{
          padding: '12px',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}
        title={
          <Space>
            <MessageOutlined style={{ color: 'var(--secondary-color)' }} />
            <Title level={5} style={{ margin: 0, color: 'var(--text-primary)', fontSize: '14px' }}>
              Chat
            </Title>
          </Space>
        }
      >
        <div 
          className="chat-container"
          style={{ 
            display: 'flex', 
            flexDirection: 'column',
            height: '100%',
            flex: 1,
            minHeight: 0
          }}
        >
          {/* Chat Messages List - Fixed height container */}
          <div 
            ref={this.chatMessagesRef}
            className="chat-messages"
            style={{ 
              flex: '1 1 auto',
              minHeight: '120px',
              overflowY: 'auto',
              marginBottom: '8px'
            }}
          >
            {chatMessages.length > 0 ? (
              <List
                size="small"
                dataSource={chatMessages}
                renderItem={(item, index) => (
                  <List.Item
                    style={{
                      padding: '6px 0',
                      borderBottom: index < chatMessages.length - 1 ? '1px solid var(--border-color)' : 'none'
                    }}
                  >
                    <div style={{ width: '100%', display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                      <Avatar 
                        name={item.sender} 
                        size={24} 
                        showBorder={false}
                        style={{ flexShrink: 0 }}
                      />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <Text style={{ 
                          color: 'var(--primary-color)', 
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          display: 'block',
                          marginBottom: 1
                        }}>
                          {item.sender}:
                        </Text>
                        <Text style={{ 
                          color: 'var(--text-secondary)', 
                          fontSize: '0.8rem',
                          lineHeight: 1.3,
                          wordWrap: 'break-word'
                        }}>
                          {item.message}
                        </Text>
                      </div>
                    </div>
                    </List.Item>
                  )}
                />
              ) : (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '16px 0',
                  color: 'var(--text-muted)',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  height: '100%'
                }}>
                  <Text style={{ display: 'block', marginBottom: 6, fontSize: '0.85rem' }}>
                    No messages yet
                  </Text>
                  <Text style={{ fontSize: '0.75rem' }}>
                    Start chatting with other players
                  </Text>
                </div>
              )}
          </div>

          <Divider style={{ borderColor: 'var(--border-color)', margin: '8px 0 4px 0', flexShrink: 0 }} />

          {/* Send New Message - Fixed height container */}
          <div style={{ 
            flexShrink: 0, 
            flex: '0 0 auto',
            backgroundColor: 'var(--bg-secondary)',
            borderRadius: 'var(--radius-md)',
            padding: '8px'
          }}>
            <TextArea
              placeholder="Type your message..."
              value={newMessage}
              onChange={this.onValueChange}
              onKeyPress={this.handleKeyPress}
              autoSize={{ minRows: 1, maxRows: 1 }}
              style={{
                background: 'var(--bg-tertiary)',
                border: '1px solid var(--border-color)',
                borderRadius: 'var(--radius-md)',
                color: 'var(--text-primary)',
                marginBottom: 6,
                fontSize: '0.8rem',
                resize: 'none'
              }}
            />
            <Button
              type="primary"
              icon={<SendOutlined />}
              size="small"
              onClick={this.sendMessage}
              disabled={!newMessage.trim()}
              style={{
                width: '100%',
                height: '28px',
                background: 'linear-gradient(135deg, var(--primary-color), var(--primary-dark))',
                border: 'none',
                borderRadius: 'var(--radius-md)',
                fontWeight: 600,
                fontSize: '0.75rem'
              }}
            >
              Send
            </Button>
          </div>
        </div>
      </Card>
    );
  }
}

export default Notes;